import { useEffect, useState } from 'react';

/**
 * Reports once an element has come near the viewport, then stops observing.
 *
 * Used to mount heavy content such as preview iframes only when the user
 * scrolls to it. Without IntersectionObserver support the element counts as
 * visible right away.
 *
 * @param element - Observed element, or `null` before it mounts.
 * @param rootMargin - Margin around the viewport that already counts as visible.
 * @returns Whether the element has entered the extended viewport.
 */
export const useHasEnteredViewport = (
  element: Element | null,
  rootMargin = '200px'
): boolean => {
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (hasEntered || !element) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setHasEntered(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasEntered(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, hasEntered, rootMargin]);

  return hasEntered;
};
